'use client';

import { apiFetch } from './api';

export interface AdminGallery {
  id: string;
  name: string;
  description: string | null;
  type: 'public' | 'client';
  accessCode: string | null;
  clientId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPhoto {
  id: string;
  galleryId: string;
  title: string;
  description: string | null;
  storagePath: string;
  fileSize: number | null;
  width: number | null;
  height: number | null;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
  url: string;
}

function readImageSize(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(src);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(src);
    };
    img.src = src;
  });
}

export const adminService = {
  // Check if the current browser session is signed in as admin
  async isAdmin(): Promise<boolean> {
    try {
      const { user } = await apiFetch<{ user: { role: string } | null }>('/api/auth/session');
      return user?.role === 'admin';
    } catch {
      return false;
    }
  },

  // Get all galleries
  async getAllGalleries(): Promise<AdminGallery[]> {
    const { galleries } = await apiFetch<{ galleries: AdminGallery[] }>('/api/admin/galleries');
    return galleries;
  },

  // Get photos by gallery (each includes a ready-to-use `url`)
  async getPhotosByGallery(galleryId: string): Promise<AdminPhoto[]> {
    const { photos } = await apiFetch<{ photos: AdminPhoto[] }>(
      `/api/admin/galleries/${encodeURIComponent(galleryId)}/photos`
    );
    return photos;
  },

  // Upload photo: get a SAS URL, PUT the file straight to Azure, then record it
  async uploadPhoto(file: File, galleryId: string, title: string, description?: string) {
    const { uploadUrl, storagePath } = await apiFetch<{ uploadUrl: string; storagePath: string }>(
      '/api/admin/photos/upload-url',
      { method: 'POST', json: { galleryId, fileName: file.name } }
    );

    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-blob-content-type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    if (!putRes.ok) {
      const detail = await putRes.text().catch(() => '');
      throw new Error(`Storage upload failed (${putRes.status}) ${detail.match(/<Message>(.*?)<\/Message>/)?.[1] ?? ''}`.trim());
    }

    const size = await readImageSize(file);
    const { photo } = await apiFetch<{ photo: AdminPhoto }>('/api/admin/photos', {
      method: 'POST',
      json: { galleryId, storagePath, title, description, width: size?.width, height: size?.height },
    });
    return photo;
  },

  // Create gallery
  async createGallery(
    name: string,
    description: string,
    type: 'public' | 'client',
    accessCode?: string,
    _clientId?: string
  ): Promise<AdminGallery> {
    const { gallery } = await apiFetch<{ gallery: AdminGallery }>('/api/admin/galleries', {
      method: 'POST',
      json: { name, description, type, accessCode },
    });
    return gallery;
  },

  // Delete gallery and all its photos
  async deleteGallery(galleryId: string) {
    await apiFetch(`/api/admin/galleries/${encodeURIComponent(galleryId)}`, { method: 'DELETE' });
  },

  // Delete photo (storage path is looked up server-side)
  async deletePhoto(photoId: string, _storagePath?: string) {
    await apiFetch(`/api/admin/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE' });
  },
};
